import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";

Chart.register(zoomPlugin);
Chart.register(annotationPlugin);

const PRICE_CHART_THRESHOLDS = {
  lowMax: 5,
  midMin: 20,
  highMin: 30,
};

const PRICE_CHART_COLORS = {
  line: "#54ACA7",
  datasetFill: "rgba(255, 255, 255, 0)",
  datasetFillTransparent: "rgba(255, 255, 255, 0)",
  shading: {
    low: "#ECF9FD",
    mid: "#FFF0CE",
    high: "#FFE8EC",
  },
  currentTime: {
    line: "#ff0000",
    labelBg: "#ff0000",
    labelText: "#ffffff",
  },
};

export default class extends Controller {
  static targets = ["canvas", "tab", "yaxis"];
  static values = {
    yesterdayData: Array,
    todayData: Array,
    tomorrowData: Array,
    shadingEnabled: Boolean,
    showCurrentTime: Boolean,
    isHighVoltagePage: Boolean,
  };

  connect() {
    this.chart = null;
    const activeTab = this.tabTargets.find((tab) =>
      tab.classList.contains("active"),
    );
    const initialTab = activeTab
      ? activeTab.getAttribute("data-tab")
      : "yesterday";
    this.#initChart(initialTab);
    this.#switchTable(initialTab);
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  switchTab(event) {
    this.tabTargets.forEach((tab) => tab.classList.remove("active"));
    event.currentTarget.classList.add("active");
    const tabKey = event.currentTarget.getAttribute("data-tab");
    this.#initChart(tabKey);
    this.#switchTable(tabKey);
  }

  #switchTable(dataKey) {
    const bodies = document.querySelectorAll(
      ".unit-price-table tbody[data-tab]",
    );
    bodies.forEach((tbody) => {
      tbody.hidden = tbody.getAttribute("data-tab") !== dataKey;
    });
  }

  #getCurrentTimeIndex() {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    return hours * 2 + (minutes >= 30 ? 1 : 0);
  }

  #generateTimeLabels() {
    const labels = [];
    for (let hour = 0; hour < 24; hour++) {
      labels.push(`${hour.toString().padStart(2, "0")}:00`);
      labels.push(`${hour.toString().padStart(2, "0")}:30`);
    }
    return labels;
  }

  get #chartData() {
    return {
      yesterday: {
        labels: this.#generateTimeLabels(),
        data: this.yesterdayDataValue || [],
        label: "昨日の料金単価",
      },
      today: {
        labels: this.#generateTimeLabels(),
        data: this.todayDataValue || [],
        label: "今日の料金単価",
      },
      tomorrow: {
        labels: this.#generateTimeLabels(),
        data: this.tomorrowDataValue || [],
        label: "明日の料金単価",
      },
    };
  }

  #classifyPrice(value) {
    if (value === null || Number.isNaN(value)) return null;
    if (value >= PRICE_CHART_THRESHOLDS.highMin)
      return PRICE_CHART_COLORS.shading.high;
    if (value >= PRICE_CHART_THRESHOLDS.midMin)
      return PRICE_CHART_COLORS.shading.mid;
    if (value < PRICE_CHART_THRESHOLDS.lowMax)
      return PRICE_CHART_COLORS.shading.low;
    return null;
  }

  #createColoredStripe(startIdx, endIdx, backgroundColor) {
    const id = `box_${startIdx}_${endIdx}`;
    return {
      [id]: {
        type: "box",
        xScaleID: "x",
        yScaleID: "y",
        xMin: startIdx,
        xMax: endIdx + 1,
        yMin: (ctx) => ctx.chart.scales.y.min,
        yMax: (ctx) => ctx.chart.scales.y.max,
        backgroundColor,
        borderColor: "rgba(0,0,0,0)",
        borderWidth: 0,
        drawTime: "beforeDraw",
      },
    };
  }

  #buildBackgroundAnnotations(values) {
    const annotations = {};
    let currentColor = null;
    let start = null;

    for (let i = 0; i < values.length; i++) {
      const color = this.#classifyPrice(values[i]);
      if (color !== currentColor) {
        if (currentColor) {
          Object.assign(
            annotations,
            this.#createColoredStripe(start, i - 1, currentColor),
          );
        }
        currentColor = color;
        start = color ? i : null;
      }
    }
    if (currentColor) {
      Object.assign(
        annotations,
        this.#createColoredStripe(start, values.length - 1, currentColor),
      );
    }
    return annotations;
  }

  #initChart(dataKey) {
    const data = this.#chartData[dataKey];
    const currentTimeIndex = this.#getCurrentTimeIndex();

    if (this.chart) {
      this.chart.destroy();
    }

    const bgAnnotations = this.shadingEnabledValue
      ? this.#buildBackgroundAnnotations(data.data)
      : {};

    const currentTimeAnnotation =
      (this.hasShowCurrentTimeValue ? this.showCurrentTimeValue : true) &&
      dataKey === "today"
        ? {
            currentTime: {
              type: "line",
              xMin: currentTimeIndex,
              xMax: currentTimeIndex,
              borderColor: PRICE_CHART_COLORS.currentTime.line,
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: "現在",
                position: "center",
                backgroundColor: "#ff0000",
                color: "#ffffff",
                font: {
                  size: 11,
                },
              },
            },
          }
        : {};

    const datasetFill = !this.shadingEnabledValue;
    const datasetBgColor = this.shadingEnabledValue
      ? PRICE_CHART_COLORS.datasetFillTransparent
      : PRICE_CHART_COLORS.datasetFill;

    this.canvasTarget.width = 2000;
    this.canvasTarget.height = 400;
    this.yAxisCopied = false;

    // Y軸の最小値は必ず0にする
    const yMin = 0;
    // 最大値はデータの最大値を5で切り上げ（例: 46→50）
    const rawMax = Math.max(...data.data);
    const yMax = Math.ceil((rawMax + 1) / 5) * 5;

    const isCoarsePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;

    const isPcViewport = window.matchMedia("(min-width: 1025px)").matches;
    const enableResponsiveForHvPc = isPcViewport && this.isHighVoltagePageValue;

    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: data.label,
            data: data.data,
            borderColor: PRICE_CHART_COLORS.line,
            backgroundColor: datasetBgColor,
            borderWidth: 2,
            fill: datasetFill,
            tension: 0,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: "#54ACA7",
          },
        ],
      },
      options: {
        responsive: enableResponsiveForHvPc,
        maintainAspectRatio: !enableResponsiveForHvPc,
        scales: {
          x: {
            min: 0,
            max: 47,
            ticks: {
              callback: function (value, index) {
                return index % 2 === 0 ? this.getLabelForValue(value) : "";
              },
            },
            offset: false,
          },
          y: {
            beginAtZero: true,
            min: yMin,
            max: yMax,
            title: {
              display: false,
            },
            ticks: {
              display: true,
              stepSize: 5,
            },
            grid: {
              display: true,
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
            onClick: false,
          },
          annotation: {
            annotations: {
              ...bgAnnotations,
              ...currentTimeAnnotation,
            },
          },
          zoom: {
            pan: {
              // スマホではネイティブの横スクロールを優先するため、パン操作を無効化
              // （ポインタがcoarse=タッチの場合はfalse、それ以外はtrue）
              enabled: !isCoarsePointer,
              mode: "x",
              onPan: ({ chart }) => {
                const xAxis = chart.scales.x;
                if (xAxis.min < 0) {
                  xAxis.min = 0;
                  xAxis.max = 23;
                }
                if (xAxis.max > 47) {
                  xAxis.min = 24;
                  xAxis.max = 47;
                }
              },
            },
            zoom: {
              // スマホでのジェスチャ競合を避けるため、ピンチズームを無効化
              // （ポインタがcoarse=タッチの場合はfalse、それ以外はtrue）
              enabled: !isCoarsePointer,
              mode: "x",
            },
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        animation: false,
      },
      plugins: [
        {
          afterRender: (chart) => this.#copyYAxis(chart, this.yaxisTarget),
        },
      ],
    });

    // チャート描画後にスクロール位置を現在時刻に合わせて調整
    this.#setInitialScrollPosition(currentTimeIndex);
  }

  #setInitialScrollPosition(currentTimeIndex) {
    requestAnimationFrame(() => {
      const scrollContainer = this.canvasTarget.parentElement;

      if (scrollContainer && scrollContainer.style.overflowX === "auto") {
        const chartArea = this.chart.chartArea;
        const chartWidth = chartArea.right - chartArea.left;

        const totalTimeSlots = 48;
        const blockWidth = chartWidth / totalTimeSlots;

        const leftMarginBlocks = 2;
        const targetBlockIndex = Math.max(
          0,
          currentTimeIndex - leftMarginBlocks,
        );

        const scrollPosition = targetBlockIndex * blockWidth;

        const maxScrollLeft =
          scrollContainer.scrollWidth - scrollContainer.clientWidth;
        const finalScrollLeft = Math.max(
          0,
          Math.min(scrollPosition, maxScrollLeft),
        );

        scrollContainer.scrollLeft = finalScrollLeft;
      }
    });
  }

  // MEMO
  // このメソッドではグラフのcanvasからY軸の部分を切り取って新しいcanvasに描画します
  // 次の記事を参考に実装しています
  // https://blog.jurabi.jp/scroll-horizontally-across-the-graph-while-displaying-the-y-axis-in-chartjs-demo-page-available/
  #copyYAxis(chart, yaxisTarget) {
    // すでにコピー済みの場合は何もしない
    if (this.yAxisCopied) {
      return;
    } else {
      this.yAxisCopied = true;
    }

    // 各種スタイル値を取得
    const scale = window.devicePixelRatio;
    const yAxScale = chart.scales.y;
    const yAxisStyleWidth0 = yAxScale.width - 10;
    const copyWidth = Math.ceil(yAxisStyleWidth0 * scale);
    const yAxisCvsWidth = copyWidth + 4;
    const yAxisStyleWidth = yAxisCvsWidth / scale;
    const yAxisStyleHeight = yAxScale.height + yAxScale.top + 10;
    const copyHeight = yAxisStyleHeight * scale;
    const yAxisCvsHeight = copyHeight;

    // Y軸のキャンバスにスタイルを適用
    const contextYAxis = yaxisTarget.getContext("2d");
    yaxisTarget.width = yAxisCvsWidth;
    yaxisTarget.height = yAxisCvsHeight;
    yaxisTarget.style.width = yAxisStyleWidth + "px";
    yaxisTarget.style.height = yAxisStyleHeight + "px";

    // Y軸のキャンバスにグラフを描画
    const canvasTarget = this.canvasTarget;
    contextYAxis.drawImage(
      canvasTarget,
      0,
      0,
      copyWidth,
      copyHeight,
      0,
      0,
      copyWidth,
      copyHeight,
    );

    // もとのcanvas内のY軸の表示を非表示にする
    chart.options.scales.y.ticks.display = false;
    chart.update();
    const contextChart = canvasTarget.getContext("2d");
    contextChart.clearRect(0, 0, yAxisStyleWidth, yAxisStyleHeight);
  }
}
